<template>
  <div class="input-richtext">
    <label v-if="label">
      <span v-if="label" v-html="label" />
    </label>
    <div
      class="editor"
      ref="editor"
      :style="computedStyle"
    />
    <span v-if="hint" class="hint" v-html="hint" />
  </div>
</template>

<script>
  export default {
    name: 'Richtext',
    components: {
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      value: {
        type: Array,
        default: () => [],
      },
      legacy: {
        type: Object,
        default: () => ({
          title: null,
          note: null,
        }),
      },
      legacyTitle: {
        type: String,
        default: null,
      },
      label: {
        type: String,
        default: null,
      },
      hint: {
        type: String,
        default: null,
      },
      imageMaxHeight: {
        type: String,
        default: 'auto',
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        currentHeight: 250,
        hasFocus: false,
        quill: null,
        preventValueWatch: false,
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      computedStyle() {
        const hasFocus = this.hasFocus;
        const currentHeight = this.currentHeight;
        const imageMaxHeight = this.imageMaxHeight;
        let out = `--max-img-height:${imageMaxHeight};`;
        out += (hasFocus) ? 'min-height:250px;' : `height:${currentHeight}px;`;
        return out;
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      value(val) {
        if (!this.preventValueWatch) {
          this.updateContent(val, null);
        }
      },
      legacy(val) {
        if (!this.preventValueWatch) {
          this.updateContent(null, val);
        }
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // render(h) { return h(); },
    mounted() {
      if (window.Quill) {
        const editorEl = this.$refs.editor;
        // eslint-disable-next-line no-undef
        const quill = new Quill(editorEl, {
          theme: 'snow',
          modules: {
            toolbar: {
              container: [['bold', 'italic'], ['image']],
            },
          },
        });

        quill.on('text-change', async () => {
          const ops = await this.parseQuillContent(quill.getContents());
          this.preventValueWatch = true;
          this.$emit('update:value', ops);
          this.$nextTick(() => {
            this.preventValueWatch = false;
          });
        });

        editorEl.firstChild.addEventListener('focusin', () => {
          this.hasFocus = true;
        });
        editorEl.firstChild.addEventListener('focusout', () => {
          const newHeight = editorEl.clientHeight + 1;
          this.currentHeight = newHeight < 250 ? 250 : newHeight;
          this.hasFocus = false;
        });

        this.quill = quill;
        this.updateContent(this.value, this.legacy);
      }
    },
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      updateContent(value, legacy) {
        // eslint-disable-next-line no-param-reassign
        value = Array.isArray(value) ? value : null;
        const quill = this.quill;
        if (value && value.length > 0) {
          quill.setContents(value);
          this.hasFocus = true;
          return;
        }

        if (legacy && legacy?.title && legacy.note) {
          let out = '';
          if (legacy?.note) {
            out = legacy.note;
          }
          if (legacy?.title) {
            out = out === '' ? `${legacy.title}` : `${out}\n${legacy.title}`;
          }
          quill.root.innerHTML = out;
        }
      },

      async parseQuillContent(content) {
        const ops = content?.ops || [];
        for (let i = 0, n = ops.length; i < n; i += 1) {
          const op = ops[i];
          if (op.insert?.image) {
            // eslint-disable-next-line no-await-in-loop
            op.insert.image = await this.optimizeImage(op.insert.image);
          }
        }
        return ops;
      },

      optimizeImage(base64Image) {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            const maxWidth = 400;
            let width = img.width;
            let height = img.height;

            if (width > maxWidth) {
              const ratio = maxWidth / width;
              width = maxWidth;
              height = Math.floor(height * ratio);
            }

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            canvas.width = width;
            canvas.height = height;

            ctx.drawImage(img, 0, 0, width, height);

            const webpBase64 = canvas.toDataURL('image/webp', 0.85);

            resolve(webpBase64);
          };

          img.onerror = () => { reject(new Error('Failed to load image')); };
          img.src = base64Image;
        });
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

$default-grid-pixels: 8px;

.input-richtext {
  & > label {
    display: block;
    @include font-body-medium;
    color: $color-black-solid-100;
    margin-bottom: 4px;

    .invalid {
      color: $color-signal-red;
    }
  }

  & > span.hint {
    display: block;
    margin-top: 4px;
    @include font-body-small;
    color: $color-black-alpha-60;
  }

  & > .editor {
    border-radius: 0 0 $default-border-radius $default-border-radius;
    border: 1px solid $color-black-alpha-40;
    transition: border $default-transition;

    & > .ql-editor img {
      max-height: var(--max-img-height, auto);
    }
  }

  & > .ql-toolbar {
    border-radius: $default-border-radius $default-border-radius 0 0;
    border: 1px solid $color-black-alpha-40;
    transition: border $default-transition;
  }

  &:focus-within {
    & > .editor {
      border: 1px solid $color-blue-alpha-80;
    }

    & > .ql-toolbar {
      border: 1px solid $color-black-alpha-40;
      border-bottom-color: $color-blue-alpha-80;
    }
  }
}
</style>
